import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { useIamCatalog } from '../../hooks/useIam';
import { AccessStateNotice } from '../../components/access/AccessStateNotice';
import { PermissionsList } from '../../components/access/permissions/PermissionsList';
import { PermissionDetailPanel } from '../../components/access/permissions/PermissionDetailPanel';

export function AccessPermissions() {
    usePageTitle('Access · Permissions');
    const { setTitle, setSubtitle } = usePageHeader();
    const navigate = useNavigate();
    const { catalog, isLoading, isError, refresh } = useIamCatalog();

    const [selectedId, setSelectedId] = useState<string | null>(null);
    useEffect(() => {
        if (selectedId || !catalog) return;
        setSelectedId(catalog.permissions[0]?.id ?? null);
    }, [catalog, selectedId]);

    useEffect(() => {
        setTitle('Permissions');
        setSubtitle(
            catalog ? `${catalog.summary.permissionCount} permissions · reverse index` : undefined
        );
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [catalog, setTitle, setSubtitle]);

    const selectedPermission = catalog?.permissions.find((p) => p.id === selectedId) ?? null;

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            {isError ? (
                <AccessStateNotice
                    title="Couldn't load the permission catalog"
                    message="GET /api/v2/iam/catalog failed to load."
                    onRetry={() => void refresh()}
                />
            ) : isLoading || !catalog ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex gap-6 px-8 pb-8">
                    <aside className="w-[280px] shrink-0 min-h-0 flex flex-col">
                        <PermissionsList
                            permissions={catalog.permissions}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                        />
                    </aside>
                    {selectedPermission ? (
                        <PermissionDetailPanel
                            // No internal state to lose, but keyed for the same
                            // structural guarantee as Roles' detail panel.
                            key={selectedId}
                            permission={selectedPermission}
                            roles={catalog.roles}
                            routes={catalog.routes}
                            onSelectRole={(key) =>
                                navigate(`/access/roles?role=${encodeURIComponent(key)}`)
                            }
                        />
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                            Select a permission
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
